import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { getClients } from '../services/api';

const TYPES = ['Email','Platform','Server','Maintenance'];
const STATUS_BADGE = { Active:'badge-green', 'Due Soon':'badge-warn', Overdue:'badge-danger', Renewed:'badge-info' };
const TYPE_BADGE = { Email:'badge-info', Platform:'badge-purple', Server:'badge-muted', Maintenance:'badge-warn' };
const EMPTY = { client_id:'', renewal_type:'Email', amount:'', renewal_date:'', notes:'' };

export default function RenewalsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter]     = useState('');
  const [monthFilter, setMonthFilter]   = useState('');
  const [yearFilter, setYearFilter]     = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [renewingId, setRenewingId]     = useState(null);
  const [form, setForm]                 = useState(EMPTY);
  const [attachFile, setAttachFile]     = useState(null);
  const [renewFile, setRenewFile]       = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data:renewData, isLoading } = useQuery({
    queryKey:['renewals', statusFilter, typeFilter, monthFilter, yearFilter],
    queryFn:()=>api.get('/renewals',{params:{status:statusFilter==='All'?undefined:statusFilter, type:typeFilter||undefined, month:monthFilter||undefined, year:yearFilter||undefined}}).then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const readFile = file => new Promise(resolve => {
    const r = new FileReader(); r.onload = e => resolve(e.target.result); r.readAsDataURL(file);
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {...form, amount:parseFloat(form.amount)||0};
      if (attachFile) { payload.attachment_file = await readFile(attachFile); payload.attachment_filename = attachFile.name; }
      return api.post('/renewals', payload);
    },
    onSuccess:()=>{ qc.invalidateQueries(['renewals']); setShowForm(false); setForm(EMPTY); setAttachFile(null); setSelectedClient(null); toast.success('Renewal added!'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });

  const updateMut = useMutation({
    mutationFn: async () => {
      const payload = {...form, amount:parseFloat(form.amount)||0};
      if (attachFile) { payload.attachment_file = await readFile(attachFile); payload.attachment_filename = attachFile.name; }
      return api.put('/renewals/'+editing.id, payload);
    },
    onSuccess:()=>{ qc.invalidateQueries(['renewals']); setEditing(null); setAttachFile(null); setSelectedClient(null); toast.success('Updated!'); },
  });

  const renewMut = useMutation({
    mutationFn: async (id) => {
      const payload = {};
      if (renewFile) { payload.attachment_file = await readFile(renewFile); payload.attachment_filename = renewFile.name; }
      return api.post('/renewals/'+id+'/renew', payload);
    },
    onSuccess:()=>{ qc.invalidateQueries(['renewals']); setRenewingId(null); setRenewFile(null); toast.success('Renewed for 1 year!'); },
  });

  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/renewals/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['renewals']); toast.success('Deleted'); },
  });

  const downloadAttachment = async (id, name) => {
    try {
      const res = await api.get('/renewals/'+id+'/attachment',{responseType:'blob'});
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href=url; a.download=name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('File not found'); }
  };

  const renewals = renewData?.data || [];
  const clients  = clientData?.data || [];

  const handleClientChange = (clientId) => {
    set('client_id', clientId);
    const client = clients.find(c=>String(c.id)===String(clientId));
    setSelectedClient(client||null);
  };

  const openEdit = r => {
    setEditing(r);
    setForm({ client_id:r.client_id, renewal_type:r.renewal_type, amount:r.amount, renewal_date:r.renewal_date, notes:r.notes||'' });
    setSelectedClient(r.client||null);
    setAttachFile(null);
    setShowForm(false);
  };

  const daysUntil = date => Math.ceil((new Date(date)-new Date())/(1000*60*60*24));

  const overdue   = renewals.filter(r=>r.status==='Overdue').length;
  const dueSoon   = renewals.filter(r=>r.status==='Due Soon').length;
  const totalAmt  = renewals.reduce((s,r)=>s+parseFloat(r.amount||0),0);

  return (
    <div>
      <div className="g4" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Renewals</div><div className="stat-val">{renewals.length}</div></div>
        <div className="card-sm"><div className="stat-label">Due Soon</div><div className="stat-val" style={{color:'var(--warn)'}}>{dueSoon}</div></div>
        <div className="card-sm"><div className="stat-label">Overdue</div><div className="stat-val" style={{color:'var(--danger)'}}>{overdue}</div></div>
        <div className="card-sm"><div className="stat-label">Total Amount</div><div className="stat-val" style={{color:'var(--green)'}}>KWD {totalAmt.toFixed(3)}</div></div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div className="tab-bar">
          {['All','Active','Due Soon','Overdue','Renewed'].map(s=>(
            <button key={s} className={"tab-btn"+(statusFilter===s?" active":"")} onClick={()=>setStatusFilter(s)}>{s}</button>
          ))}
        </div>
        <select className="form-select" style={{width:140}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
          <option value="">All Types</option>
          {TYPES.map(t=><option key={t}>{t}</option>)}
        </select>
        {typeFilter&&<button className="btn btn-outline btn-sm" onClick={()=>setTypeFilter('')}>Clear</button>}
        <select className="form-select" style={{width:130}} value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}>
          <option value="">All Months</option>
          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i)=><option key={i+1} value={String(i+1)}>{m}</option>)}
        </select>
        <select className="form-select" style={{width:90}} value={yearFilter} onChange={e=>setYearFilter(e.target.value)}>
          <option value="">Year</option>
          {['2024','2025','2026','2027','2028'].map(y=><option key={y}>{y}</option>)}
        </select>
        {(monthFilter||yearFilter)&&<button className="btn btn-outline btn-sm" onClick={()=>{setMonthFilter('');setYearFilter('');}}>Clear</button>}
        <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setSelectedClient(null);setAttachFile(null);}}>+ Add Renewal</button>
      </div>

      {/* Add/Edit Form */}
      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Renewal':'New Renewal'}</div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e=>handleClientChange(e.target.value)}>
                <option value="">Select client...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Renewal Type *</label>
              <select className="form-select" value={form.renewal_type} onChange={e=>set('renewal_type',e.target.value)}>
                {TYPES.map(t=><option key={t}>{t}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Renewal Date *</label>
              <input className="form-input" type="date" value={form.renewal_date} onChange={e=>set('renewal_date',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Amount (KWD)</label>
              <input className="form-input" type="number" min="0" step="0.001" value={form.amount} onChange={e=>set('amount',e.target.value)} placeholder="0.000"/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Upload Attachment</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e=>setAttachFile(e.target.files[0])}
                style={{padding:'8px 12px',background:'#fff',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',width:'100%',fontSize:13}}/>
              {attachFile&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>Selected: {attachFile.name}</div>}
              {editing&&editing.attachment_name&&!attachFile&&<div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Current: {editing.attachment_name}</div>}
            </div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Notes..."/></div>
          </div>

          {/* Client Info Preview */}
          {selectedClient && (
            <div style={{background:'linear-gradient(135deg,#f0fdf7,#e8f8f1)',border:'1.5px solid #86efac',borderRadius:12,padding:16,margin:'16px 0'}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--green)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Client Details</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Name</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.name}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Phone</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.phone||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Email</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.email||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Company</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.company||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Civil ID</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.civil_id||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Address</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.address||'--'}</div></div>
              </div>
              {form.renewal_date && (
                <div style={{marginTop:12,padding:'8px 12px',background:'#fff',borderRadius:8,fontSize:12,color:'var(--muted)'}}>
                  Next renewal will be auto-set to: <strong style={{color:'var(--green)'}}>
                    {new Date(new Date(form.renewal_date).setFullYear(new Date(form.renewal_date).getFullYear()+1)).toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}
                  </strong>
                </div>
              )}
            </div>
          )}

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={()=>editing?updateMut.mutate():createMut.mutate()}>
              {editing?'Update Renewal':'Save Renewal'}
            </button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setAttachFile(null);setSelectedClient(null);}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Renew Modal */}
      {renewingId && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:16,padding:28,width:440,boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>Renew for 1 Year</div>
            <div style={{fontSize:13,color:'var(--muted)',marginBottom:20}}>
              This will automatically set the next renewal date to <strong>+1 year</strong> from today.
            </div>
            <div style={{marginBottom:16}}>
              <label className="form-label" style={{marginBottom:8,display:'block'}}>Upload Renewal Attachment (optional)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e=>setRenewFile(e.target.files[0])}
                style={{padding:'8px 12px',background:'#f8fafb',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)',width:'100%',fontSize:13}}/>
              {renewFile&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>Selected: {renewFile.name}</div>}
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary" style={{flex:1}} onClick={()=>renewMut.mutate(renewingId)}>
                ✓ Confirm Renewal
              </button>
              <button className="btn btn-outline" onClick={()=>{setRenewingId(null);setRenewFile(null);}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead>
              <tr><th>Client</th><th>Type</th><th>Renewal Date</th><th>Next Renewal</th><th>Days Left</th><th>Amount</th><th>Status</th><th>Attachment</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {renewals.length===0&&<tr><td colSpan={9} className="empty-state">No renewals found</td></tr>}
              {renewals.map(r=>{
                const days = r.next_renewal_date ? daysUntil(r.next_renewal_date) : null;
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{fontWeight:600}}>{r.client?.name||'--'}</div>
                      <div style={{fontSize:11,color:'var(--muted)'}}>{r.client?.phone||''}</div>
                      <div style={{fontSize:11,color:'var(--muted)'}}>{r.client?.email||''}</div>
                    </td>
                    <td><span className={"badge "+(TYPE_BADGE[r.renewal_type]||'badge-muted')}>{r.renewal_type}</span></td>
                    <td style={{color:'var(--muted)',fontSize:12}}>{r.renewal_date}</td>
                    <td style={{fontSize:12,fontWeight:500}}>{r.next_renewal_date||'--'}</td>
                    <td>
                      {days!==null
                        ?<span style={{fontWeight:700,fontSize:13,color:days<0?'var(--danger)':days<=30?'var(--warn)':'var(--green)'}}>
                          {days<0?Math.abs(days)+'d overdue':days===0?'Today':days+'d'}
                        </span>
                        :'--'
                      }
                    </td>
                    <td style={{fontWeight:600,color:'var(--green)'}}>KWD {parseFloat(r.amount||0).toFixed(3)}</td>
                    <td><span className={"badge "+(STATUS_BADGE[r.status]||'badge-muted')}>{r.status}</span></td>
                    <td>{r.attachment_name
                      ?<button className="btn btn-outline btn-sm" style={{color:'var(--green)',borderColor:'var(--green)'}} onClick={()=>downloadAttachment(r.id,r.attachment_name)}>Download</button>
                      :<span style={{color:'#ccc',fontSize:12}}>--</span>}
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                        {(r.status==='Due Soon'||r.status==='Overdue') && (
                          <button className="btn btn-primary btn-sm" onClick={()=>setRenewingId(r.id)}>
                            ↻ Renew
                          </button>
                        )}
                        <button className="btn btn-outline btn-sm" onClick={()=>openEdit(r)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteMut.mutate(r.id);}}>X</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
